import api from './api';

export const loginAdmin = async (email, password) => {
  try {
    const response = await api.post('admin-login/', {
      email: email.trim(),
      password: password.trim(),
    });
    return { success: true, data: response.data };
  } catch (error) {
    const errorMsg =
      error.response?.data?.detail ||
      error.response?.data?.error ||
      'Login failed. Please check your credentials.';
    return { success: false, error: errorMsg };
  }
};

export const logoutAdmin = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
};

export const refreshAccessToken = async () => {
  try {
    const refresh = localStorage.getItem('refresh_token');
    if (!refresh) throw new Error('No refresh token found');

    const response = await api.post('token/refresh/', { refresh });

    if (response.data.access) {
      localStorage.setItem('access_token', response.data.access);
    }
    return response.data;
  } catch (error) {
    logoutAdmin();
    throw error;
  }
};

export const submitSolarQuotation = async (formData) => {
  try {
    const mappedData = {
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      property_type: formData.propertyType,
      monthly_bill: formData.monthlyBill,
      location: formData.location,
    };

    const response = await api.post('quotation/', mappedData);
    return { success: true, message: response.data.message };
  } catch (error) {
    let errorMsg = 'Failed to submit request. Please try again.';
    if (error.response && error.response.data) {
      const errors = error.response.data.errors || error.response.data;
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorDetail = errors[firstErrorKey];
        errorMsg = Array.isArray(errorDetail) ? errorDetail[0] : errorDetail;
      }
    }
    return { success: false, error: errorMsg };
  }
};

export const submitContactInquiry = async (formData) => {
  try {
    const response = await api.post('contact/', {
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim() || null, 
      subject: formData.subject.trim() || "No Subject Specified",
      message: formData.message.trim(),
    });
    return { success: true, message: response.data.message };
  } catch (error) {
    let errorMsg = 'Failed to send message. Please try again later.';
    if (error.response && error.response.data) {
      const errors = error.response.data.errors || error.response.data;
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorDetail = errors[firstErrorKey];
        errorMsg = Array.isArray(errorDetail) ? errorDetail[0] : errorDetail;
      }
    }
    return { success: false, error: errorMsg };
  }
};


export const fetchPublicProjects = async () => {
  try {
    const response = await api.get('projects/');
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: 'Failed to load projects.' };
  }
};


export const fetchPublicProjectDetail = async (id) => {
  try {
    const response = await api.get(`projects/${id}/`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: 'Project not found.' };
  }
};

export const fetchAdminProjects = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    
    if (filters.search) {
      params.append('search', filters.search);
    }
    if (filters.status && filters.status !== 'All') {
      params.append('status', filters.status);
    }
    if (filters.category && filters.category !== 'All') {
      params.append('category', filters.category);
    }
    const queryString = params.toString() ? `?${params.toString()}` : '';

    const response = await api.get(`admin/projects/${queryString}`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: 'Failed to load admin projects.' };
  }
};

export const fetchAdminProjectDetail = async (id) => {
  try {
    const response = await api.get(`admin/projects/${id}/`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: 'Project not found.' };
  }
};
export const createAdminProject = async (formData, imageFile) => {
  try {
    const data = new FormData();
    data.append('title', formData.title);
    data.append('customer', formData.customer);
    data.append('category', formData.category);
    data.append('tag', formData.tag);
    data.append('location', formData.location);
    data.append('capacity', formData.capacity);
    data.append('savings', formData.savings);
    data.append('amount', formData.amount);
    if (formData.startDate) data.append('start_date', formData.startDate);
    if (formData.completionDate) data.append('completion_date', formData.completionDate);
    data.append('status', formData.status);
    data.append('description', formData.description);
    data.append('panels', formData.panels);
    data.append('inverter', formData.inverter);
    data.append('battery', formData.battery);
    data.append('roof_type', formData.roofType);

    if (imageFile) {
      data.append('image', imageFile);
    }

    const response = await api.post('admin/projects/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { success: true, message: response.data.message, data: response.data.project };
  } catch (error) {
    let errorMsg = 'Failed to create project.';
    if (error.response?.data?.errors) {
      const firstKey = Object.keys(error.response.data.errors)[0];
      errorMsg = `${firstKey}: ${error.response.data.errors[firstKey]}`;
    }
    return { success: false, error: errorMsg };
  }
};


export const updateAdminProject = async (id, formData, imageFile) => {
  try {
    const data = new FormData();
    data.append('title', formData.title);
    data.append('customer', formData.customer);
    data.append('category', formData.category);
    data.append('tag', formData.tag);
    data.append('location', formData.location);
    data.append('capacity', formData.capacity);
    data.append('savings', formData.savings);
    data.append('amount', formData.amount);
    if (formData.startDate) data.append('start_date', formData.startDate);
    if (formData.completionDate) data.append('completion_date', formData.completionDate);
    data.append('status', formData.status);
    data.append('description', formData.description);
    data.append('panels', formData.panels);
    data.append('inverter', formData.inverter);
    data.append('battery', formData.battery);
    data.append('roof_type', formData.roofType);
    if (imageFile) {
      data.append('image', imageFile);
    }

    const response = await api.put(`admin/projects/${id}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { success: true, message: response.data.message, data: response.data.project };
  } catch (error) {
    let errorMsg = 'Failed to update project.';
    if (error.response?.data?.errors) {
      const firstKey = Object.keys(error.response.data.errors)[0];
      errorMsg = `${firstKey}: ${error.response.data.errors[firstKey]}`;
    }
    return { success: false, error: errorMsg };
  }
};

// Admin: Delete Project
export const deleteAdminProject = async (id) => {
  try {
    const response = await api.delete(`admin/projects/${id}/`);
    return { success: true, message: response.data.message };
  } catch (error) {
    return { success: false, error: 'Failed to delete project.' };
  }
};


export const fetchAdminEnquiries = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.resolved !== undefined && filters.resolved !== 'All') {
      params.append('resolved', filters.resolved === 'Resolved' ? 'true' : 'false');
    }

    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`admin/contacts/${queryString}`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: 'Failed to load enquiries.' };
  }
};

// 🔒 Admin: Toggle Resolved Status of Enquiry
export const updateEnquiryResolveStatus = async (id, isResolved) => {
  try {
    const response = await api.put(`admin/contacts/${id}/`, { is_resolved: isResolved });
    return { success: true, message: response.data.message, is_resolved: response.data.is_resolved };
  } catch (error) {
    return { success: false, error: 'Failed to update enquiry status.' };
  }
};

// 🔒 Admin: Delete Contact Enquiry
export const deleteAdminEnquiry = async (id) => {
  try {
    const response = await api.delete(`admin/contacts/${id}/`);
    return { success: true, message: response.data.message };
  } catch (error) {
    return { success: false, error: 'Failed to delete enquiry.' };
  }
};

// Fetch all employees with filters
export const fetchAdminEmployees = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.role && filters.role !== 'All') params.append('role', filters.role);
    if (filters.status && filters.status !== 'All') params.append('status', filters.status);

    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`admin/employees/${queryString}`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: 'Failed to load employees.' };
  }
};

// Get single employee for editing
export const fetchAdminEmployeeDetail = async (id) => {
  try {
    const response = await api.get(`admin/employees/${id}/`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: 'Employee not found.' };
  }
};

// Create new employee (with photo upload)
export const createAdminEmployee = async (formData, photoFile) => {
  try {
    const data = new FormData();
    data.append('name', formData.name);
    data.append('role', formData.role);
    data.append('phone', formData.phone);
    data.append('email', formData.email);
    if (formData.address) data.append('address', formData.address);
    if (formData.joiningDate) data.append('joining_date', formData.joiningDate);
    data.append('status', formData.status || 'Active');
    data.append('assigned_projects', formData.assignedProjects || 0);
    if (photoFile) data.append('photo', photoFile);

    const response = await api.post('admin/employees/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { success: true, message: response.data.message, data: response.data.employee };
  } catch (error) {
    let errorMsg = 'Failed to add employee.';
    if (error.response?.data?.errors) {
      const firstKey = Object.keys(error.response.data.errors)[0];
      errorMsg = `${firstKey}: ${error.response.data.errors[firstKey]}`;
    }
    return { success: false, error: errorMsg };
  }
};

// Update existing employee
export const updateAdminEmployee = async (id, formData, photoFile) => {
  try {
    const data = new FormData();
    data.append('name', formData.name);
    data.append('role', formData.role);
    data.append('phone', formData.phone);
    data.append('email', formData.email);
    if (formData.address) data.append('address', formData.address);
    if (formData.joiningDate) data.append('joining_date', formData.joiningDate);
    data.append('status', formData.status || 'Active');
    data.append('assigned_projects', formData.assignedProjects || 0);
    if (photoFile) data.append('photo', photoFile);

    const response = await api.put(`admin/employees/${id}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { success: true, message: response.data.message, data: response.data.employee };
  } catch (error) {
    let errorMsg = 'Failed to update employee.';
    if (error.response?.data?.errors) {
      const firstKey = Object.keys(error.response.data.errors)[0];
      errorMsg = `${firstKey}: ${error.response.data.errors[firstKey]}`;
    }
    return { success: false, error: errorMsg };
  }
};

// Delete employee
export const deleteAdminEmployee = async (id) => {
  try {
    const response = await api.delete(`admin/employees/${id}/`);
    return { success: true, message: response.data.message };
  } catch (error) {
    return { success: false, error: 'Failed to remove employee.' };
  }
};

export const fetchPublicTestimonials = async () => {
  try {
    const response = await api.get('testimonials/');
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: 'Failed to load testimonials.' };
  }
};

export const fetchAdminTestimonials = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.is_active !== undefined && filters.is_active !== 'All') {
      params.append('is_active', filters.is_active === 'Active' ? 'true' : 'false');
    }
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`admin/testimonials/${queryString}`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: 'Failed to load testimonials.' };
  }
};

export const fetchAdminTestimonialDetail = async (id) => {
  try {
    const response = await api.get(`admin/testimonials/${id}/`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: 'Testimonial not found.' };
  }
};

export const createAdminTestimonial = async (formData, imageFile) => {
  try {
    const data = new FormData();
    data.append('name', formData.name);
    data.append('role', formData.role);
    data.append('review', formData.review);
    data.append('rating', formData.rating);
    data.append('is_active', formData.is_active);
    data.append('display_order', formData.display_order || 0);
    if (imageFile) data.append('image', imageFile);

    const response = await api.post('admin/testimonials/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { success: true, message: response.data.message, data: response.data.testimonial };
  } catch (error) {
    let errorMsg = 'Failed to add testimonial.';
    if (error.response?.data?.errors) {
      const firstKey = Object.keys(error.response.data.errors)[0];
      errorMsg = `${firstKey}: ${error.response.data.errors[firstKey]}`;
    }
    return { success: false, error: errorMsg };
  }
};

export const updateAdminTestimonial = async (id, formData, imageFile) => {
  try {
    const data = new FormData();
    data.append('name', formData.name);
    data.append('role', formData.role);
    data.append('review', formData.review);
    data.append('rating', formData.rating);
    data.append('is_active', formData.is_active);
    data.append('display_order', formData.display_order || 0);
    if (imageFile) data.append('image', imageFile);

    const response = await api.put(`admin/testimonials/${id}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { success: true, message: response.data.message, data: response.data.testimonial };
  } catch (error) {
    let errorMsg = 'Failed to update testimonial.';
    if (error.response?.data?.errors) {
      const firstKey = Object.keys(error.response.data.errors)[0];
      errorMsg = `${firstKey}: ${error.response.data.errors[firstKey]}`;
    }
    return { success: false, error: errorMsg };
  }
};

export const deleteAdminTestimonial = async (id) => {
  try {
    const response = await api.delete(`admin/testimonials/${id}/`);
    return { success: true, message: response.data.message };
  } catch (error) {
    return { success: false, error: 'Failed to delete testimonial.' };
  }
};